import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import spinner from "../layout/spinner.gif";
import { Toggle } from "./AlertCard";
import {
	fieldClass,
	areaClass,
	labelClass,
	primaryButton,
	secondaryButton,
	smallButton,
} from "../template/templateLabels";

// What cloudprint.mn/advertise shows businesses about ads on the kiosk screens: a
// text, facts such as the screen and the price, and the contacts (the backend's
// utils/ads.js). Turned on, the home page shows a card that leads there. A form of
// its own, saved apart from the mails above.

const PAGE_URL = "https://cloudprint.mn/advertise";
// As the backend checks them
const LEAD_MAX = 300;
const FACTS_MAX = 8;
const PHONES_MAX = 4;

// A backend without /api/ads answers with the admin's index.html and a 200, so
// anything but the record counts as not loaded
const isAds = (value) =>
	Boolean(value) &&
	typeof value === "object" &&
	typeof value.lead === "string" &&
	Array.isArray(value.facts) &&
	Array.isArray(value.phones);

const formOf = (ads) => ({
	enabled: ads.enabled,
	lead: ads.lead,
	facts: ads.facts.map(({ label, value }) => ({ label, value })),
	phones: [...ads.phones],
	email: ads.email,
	facebook: ads.facebook,
});

// The rows left empty are not sent
const bodyOf = (form) => ({
	...form,
	facts: form.facts.filter(({ label, value }) => label.trim() || value.trim()),
	phones: form.phones.filter((phone) => phone.trim()),
});

const card = "bg-white rounded-lg shadow-sm dark:bg-gray-800";
const removeButton =
	"flex items-center justify-center flex-shrink-0 w-10 h-10 text-gray-400 rounded-lg hover:text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-200";

const AdSettings = ({ getAds, saveAds, onDirty }) => {
	// loading | ready | failed
	const [state, setState] = useState("loading");
	const [ads, setAds] = useState(null);
	const [form, setForm] = useState(null);
	const [saving, setSaving] = useState(false);

	const load = async () => {
		setState("loading");
		const loaded = await getAds();
		if (!isAds(loaded)) {
			setState("failed");
			return;
		}
		setAds(loaded);
		setForm(formOf(loaded));
		setState("ready");
	};

	useEffect(() => {
		load();
		// Once, when the page opens
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const saved = useMemo(() => (ads ? formOf(ads) : null), [ads]);
	const dirty = Boolean(form && saved && JSON.stringify(form) !== JSON.stringify(saved));

	useEffect(() => {
		onDirty(dirty);
	}, [dirty, onDirty]);

	if (state === "loading") {
		return (
			<img
				src={spinner}
				className="my-6"
				style={{ width: "50px", margin: "auto", display: "block" }}
				alt="Loading..."
			/>
		);
	}

	if (state === "failed") {
		return (
			<div className={"flex flex-wrap items-center justify-between gap-4 p-5 " + card}>
				<p className="text-sm text-gray-600 dark:text-gray-300">
					<i className="mr-2 text-red-500 fas fa-exclamation-circle" />
					Рекламын тохиргоог ачаалж чадсангүй
				</p>
				<button type="button" className={secondaryButton} onClick={load}>
					Дахин оролдох
				</button>
			</div>
		);
	}

	const body = bodyOf(form);
	// The backend refuses these too; here the button says so before
	const problem = !body.lead.trim()
		? "Тайлбар бичнэ үү"
		: body.enabled && body.phones.length === 0 && !body.email.trim() && !body.facebook.trim()
		? "Асаахын өмнө утас, имэйл, Facebook-ийн аль нэгийг нэмнэ үү"
		: null;
	const canSave = dirty && !saving && !problem;

	const set = (field, value) => setForm({ ...form, [field]: value });
	const setFact = (index, field, value) =>
		set(
			"facts",
			form.facts.map((fact, i) => (i === index ? { ...fact, [field]: value } : fact)),
		);
	const setPhone = (index, value) =>
		set(
			"phones",
			form.phones.map((phone, i) => (i === index ? value : phone)),
		);

	const save = async () => {
		if (!canSave) return;
		setSaving(true);
		const result = await saveAds(body);
		setSaving(false);
		if (isAds(result.ads)) {
			setAds(result.ads);
			setForm(formOf(result.ads));
		}
	};

	return (
		<div className={card}>
			<div className="flex items-start gap-4 p-5 border-b border-gray-100 dark:border-gray-700">
				<div className="flex-1 min-w-0">
					<div className="flex flex-wrap items-center gap-2">
						<h4 className="font-semibold text-gray-700 dark:text-gray-200">Рекламын хуудас</h4>
						{!form.enabled && (
							<span className="px-2 py-0.5 text-xs text-gray-600 bg-gray-100 rounded-full dark:text-gray-300 dark:bg-gray-700">
								Унтраастай
							</span>
						)}
					</div>
					<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
						Асаавал нүүр хуудсанд карт гарч,{" "}
						<a
							href={PAGE_URL}
							target="_blank"
							rel="noopener noreferrer"
							className="text-purple-600 hover:underline dark:text-purple-300"
						>
							cloudprint.mn/advertise
						</a>{" "}
						нээгдэнэ. Киоскууд реклам харуулж эхлэхэд асаана.
					</p>
				</div>
				<Toggle
					checked={form.enabled}
					onChange={(enabled) => set("enabled", enabled)}
					label="Рекламын хуудас"
				/>
			</div>

			<div className="p-5 space-y-6">
				<label className="block">
					<span className={labelClass}>Тайлбар</span>
					<textarea
						rows="3"
						maxLength={LEAD_MAX}
						value={form.lead}
						onChange={(e) => set("lead", e.target.value)}
						className={areaClass}
					/>
					<span className="block mt-1 text-xs text-right text-gray-400">
						{form.lead.length}/{LEAD_MAX}
					</span>
				</label>

				<div>
					<p className={labelClass}>Мэдээлэл</p>
					<p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
						Зар хаана, ямар хэмжээгээр гарах, формат, үнэ гэх мэт. Хуудсанд карт болж харагдана.
					</p>
					<div className="space-y-2">
						{form.facts.map((fact, index) => (
							<div key={index} className="flex gap-2">
								<input
									value={fact.label}
									onChange={(e) => setFact(index, "label", e.target.value)}
									maxLength={40}
									placeholder="Байршил"
									aria-label="Нэр"
									className={fieldClass}
									style={{ flex: "0 0 38%" }}
								/>
								<input
									value={fact.value}
									onChange={(e) => setFact(index, "value", e.target.value)}
									maxLength={80}
									placeholder="Баруун дээд булан, 16:9"
									aria-label="Утга"
									className={fieldClass}
								/>
								<button
									type="button"
									className={removeButton}
									onClick={() =>
										set(
											"facts",
											form.facts.filter((_, i) => i !== index),
										)
									}
									aria-label={(fact.label || "Мэдээлэл") + " хасах"}
								>
									<i className="fas fa-times" />
								</button>
							</div>
						))}
					</div>
					<button
						type="button"
						className={
							smallButton +
							" mt-2" +
							(form.facts.length >= FACTS_MAX ? " opacity-50 cursor-not-allowed" : "")
						}
						disabled={form.facts.length >= FACTS_MAX}
						onClick={() => set("facts", [...form.facts, { label: "", value: "" }])}
					>
						<i className="mr-1.5 fas fa-plus" />
						Мэдээлэл нэмэх
					</button>
				</div>

				<div className="grid gap-6 sm:grid-cols-2">
					<div>
						<p className={labelClass}>Утас</p>
						<div className="space-y-2">
							{form.phones.map((phone, index) => (
								<div key={index} className="flex gap-2">
									<input
										type="tel"
										inputMode="numeric"
										value={phone}
										onChange={(e) => setPhone(index, e.target.value)}
										placeholder="8898 7266"
										aria-label="Утасны дугаар"
										className={fieldClass}
									/>
									<button
										type="button"
										className={removeButton}
										onClick={() =>
											set(
												"phones",
												form.phones.filter((_, i) => i !== index),
											)
										}
										aria-label={(phone || "Утас") + " хасах"}
									>
										<i className="fas fa-times" />
									</button>
								</div>
							))}
						</div>
						<button
							type="button"
							className={
								smallButton +
								" mt-2" +
								(form.phones.length >= PHONES_MAX ? " opacity-50 cursor-not-allowed" : "")
							}
							disabled={form.phones.length >= PHONES_MAX}
							onClick={() => set("phones", [...form.phones, ""])}
						>
							<i className="mr-1.5 fas fa-plus" />
							Утас нэмэх
						</button>
					</div>

					<div className="space-y-4">
						<label className="block">
							<span className={labelClass}>Имэйл</span>
							<input
								type="email"
								value={form.email}
								onChange={(e) => set("email", e.target.value)}
								placeholder="Хоосон бол харагдахгүй"
								className={fieldClass}
							/>
						</label>
						<label className="block">
							<span className={labelClass}>Facebook</span>
							<input
								type="url"
								value={form.facebook}
								onChange={(e) => set("facebook", e.target.value)}
								placeholder="https://www.facebook.com/..."
								className={fieldClass}
							/>
						</label>
					</div>
				</div>
			</div>

			<div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-t border-gray-100 dark:border-gray-700">
				<p className="text-xs text-gray-500 dark:text-gray-400">
					{problem && dirty ? (
						<span className="text-red-600 dark:text-red-400">
							<i className="mr-2 fas fa-exclamation-circle" />
							{problem}
						</span>
					) : ads.updatedAt ? (
						<>
							<i className="mr-2 fas fa-history" />
							Сүүлд {ads.updatedBy ? ads.updatedBy + " " : ""}
							{moment(ads.updatedAt).format("YYYY-MM-DD HH:mm")}-д хадгалсан
						</>
					) : (
						"Хадгалаагүй байна"
					)}
				</p>
				<div className="flex gap-2">
					<button
						type="button"
						className={secondaryButton + (dirty && !saving ? "" : " opacity-50 cursor-not-allowed")}
						onClick={() => setForm(saved)}
						disabled={!dirty || saving}
					>
						Буцаах
					</button>
					<button
						type="button"
						className={
							primaryButton +
							" inline-flex items-center" +
							(canSave ? "" : " opacity-50 cursor-not-allowed")
						}
						onClick={save}
						disabled={!canSave}
					>
						{saving && <i className="mr-2 fas fa-spinner fa-spin" />}
						Хадгалах
					</button>
				</div>
			</div>
		</div>
	);
};

export default AdSettings;
